import { createRouter, createWebHistory } from 'vue-router'
import { getToken } from '../utils/storage'
import { APP_CONFIG } from '../config/index'
import { useGlobalStore } from '../store/global'
import { useUserStore } from '../store/user'
import { clearPendingRequests } from '../api/request'
import { allRoutes } from '../config/routesConfig.js'
import { showToast } from 'vant'
import { showLoginConfirm } from '@/utils/dialogHandler'

const router = createRouter({
  history: createWebHistory(),
  routes: allRoutes
})

const authOnlyPaths = ['/login', '/register', '/forget-password']

const roleMap = {
  'system': 'system',
  'brand': 'brand',
  'store': 'store',
  'assign': 'assign',
  'follow': 'follow'
}

/**
 * 检测是否在企微客户端环境
 */
const isWecomEnv = () => {
  const ua = navigator.userAgent.toLowerCase()
  return ua.includes('wxwork') || ua.includes('wecom')
}

/**
 * 从企微通知跳转来的用户：调用后端接口判断登录状态
 * - 已登录：后端返回目标页面URL（带token），跳转后前端保存token
 * - 未登录：后端返回OAuth授权URL，发起企微授权
 */
const handleWecomRedirect = async (to) => {
  try {
    const { getWechatWorkH5AuthUrl } = await import('../api/models/oauth')
    const targetPath = to.fullPath
    const state = `wechat_work_h5|${targetPath}`
    console.log('[企微跳转] 调用h5-auth-url, state:', state)
    const res = await getWechatWorkH5AuthUrl({ state })
    console.log('[企微跳转] 返回结果:', res)
    if (res.data && res.data.url) {
      console.log('[企微跳转] 跳转到:', res.data.url, '已登录:', res.data.logged_in)
      // 使用replace避免历史记录堆积
      window.location.replace(res.data.url)
      return true
    }
  } catch (e) {
    console.error('企微授权处理失败:', e)
  }
  return false
}

router.beforeEach(async (to, from, next) => {
  const pageTitle = to.meta.title
  const appTitle = APP_CONFIG.TITLE || ''
  document.title = appTitle ? `${pageTitle}_${appTitle}` : pageTitle

  clearPendingRequests()

  const isLoggedIn = !!getToken()
  const requiresAuth = to.meta.requiresAuth
  const routeRoles = to.meta.roles

  const globalStore = useGlobalStore()
  globalStore.resetLoading()

  // 处理企微通知跳转（redirect=wecom）
  // 只有未登录用户才需要处理，已登录用户在main.js中已清理参数
  if (to.query.redirect === 'wecom' && !isLoggedIn) {
    const redirectHandled = await handleWecomRedirect(to)
    if (redirectHandled) return
    // 如果handleWecomRedirect失败，继续正常流程
  }

  // 需要登录但未登录的页面，弹窗提示而非直接跳转
  if (requiresAuth && !isLoggedIn) {
    globalStore.setNeedLogin(true)
    showLoginConfirm('functionNeedLogin').catch(() => {})
    // 阻止导航，留在当前页面
    next(false)
    return
  }

  // 已登录用户访问登录/注册页，跳转到首页
  // 但如果目标路径是 /login 且带有 logout 参数，允许访问
  if (isLoggedIn && authOnlyPaths.includes(to.path) && !to.query.logout) {
    globalStore.setNeedLogin(false)
    next({ path: '/home' })
    return
  }

  if (routeRoles && routeRoles.length > 0 && isLoggedIn) {
    const userStore = useUserStore()
    const userRole = userStore.userInfo?.user_role
    
    if (userRole && !routeRoles.includes(userRole)) {
      showToast('无权访问该页面')
      if (from.path && from.path !== '/' && !authOnlyPaths.includes(from.path)) {
        next(false)
      } else {
        next({ path: '/home' })
      }
      return
    }
  }

  globalStore.setNeedLogin(false)
  next()
})

export default router
